import { QueryClient, useMutation } from "@tanstack/react-query"
import { apiErrorMessage } from "../../../services/apiErrorText"
import { messageError, messageSuccess } from "../../../services/messages"
import {
    addStopToTour,
    createServiceCatalogItem,
    createTour,
    deleteServiceCatalogItem,
    deleteTour,
    deleteTourTasksForObject,
    generateTourTasks,
    updateTourObjectTask,
    moveStopOrder,
    moveStopToOtherTour,
    moveStopToWeekday,
    removeStopFromTour,
    updateServiceCatalogItem,
    updateTour,
} from "./apiCalls"
import { TourObjectRef, TourObjectTaskStatus, TourStop, TourWeekday } from "../types"

const refresh = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = String(query.queryKey[0] ?? "")
            return key.startsWith("tour") || key.startsWith("singleTour")
        },
    })
}

export const useCreateTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { name: string; employee?: string }) => createTour(data),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-create", "Tour angelegt", "Die Tour wurde gespeichert.")
        },
        onError: (err) => {
            messageError("tour-create", "Fehler!", apiErrorMessage(err, "Die Tour konnte nicht angelegt werden."))
        },
    })

export const useUpdateTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { id: string; name: string; active: boolean; employee?: string | null }) =>
            updateTour(data.id, { name: data.name, active: data.active, employee: data.employee }),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-update", "Tour gespeichert", "Name, Mitarbeiter und Status wurden übernommen.")
        },
        onError: (err) => {
            messageError("tour-update", "Fehler!", apiErrorMessage(err, "Die Tour konnte nicht gespeichert werden."))
        },
    })

export const useDeleteTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (id: string) => deleteTour(id),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-delete", "Tour entfernt", "Die Tour wurde gelöscht.")
        },
        onError: (err) => {
            messageError("tour-delete", "Fehler!", apiErrorMessage(err, "Die Tour konnte nicht gelöscht werden."))
        },
    })

export const useAddTourStopMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; object: TourObjectRef; weekday: TourWeekday; extra?: Partial<TourStop> }) =>
            addStopToTour(data.tourId, data.object, data.weekday, data.extra),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-stop-add", "Objekt zugeordnet", "Das Objekt liegt auf dem gewählten Wochentag.")
        },
        onError: (err) => {
            messageError("tour-stop-add", "Fehler!", apiErrorMessage(err, "Das Objekt konnte der Tour nicht zugeordnet werden."))
        },
    })

export const useRemoveTourStopMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; stopId: string }) => removeStopFromTour(data.tourId, data.stopId),
        onSuccess: () => refresh(queryClient),
        onError: (err) => {
            messageError("tour-stop-remove", "Fehler!", apiErrorMessage(err, "Das Objekt konnte nicht entfernt werden."))
        },
    })

export const useReorderTourStopMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; stopId: string; direction: -1 | 1 }) => moveStopOrder(data.tourId, data.stopId, data.direction),
        onSuccess: () => refresh(queryClient),
    })

export const useMoveTourStopWeekdayMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; stopId: string; weekday: TourWeekday }) => moveStopToWeekday(data.tourId, data.stopId, data.weekday),
        onSuccess: () => refresh(queryClient),
    })

export const useMoveTourStopToTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { fromTourId: string; toTourId: string; stopId: string; weekday: TourWeekday }) =>
            moveStopToOtherTour(data.fromTourId, data.toTourId, data.stopId, data.weekday),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-move", "Objekt verschoben", "Das Objekt liegt jetzt auf der anderen Tour.")
        },
        onError: (err) => {
            messageError("tour-move", "Fehler!", apiErrorMessage(err, "Das Objekt konnte nicht verschoben werden."))
        },
    })

const refreshCatalog = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = String(query.queryKey[0] ?? "")
            return key.startsWith("serviceCatalog") || key.startsWith("tourTasks")
        },
    })
}

export const useCreateServiceCatalogItemMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: {
            objectId: string
            activity: string
            description?: string
            intervalWeeks: number
            delayWeeks: number
            active: boolean
        }) => createServiceCatalogItem(data),
        onSuccess: () => {
            refreshCatalog(queryClient)
            messageSuccess("catalog-create", "Leistung gespeichert", "Die Position liegt am Objekt. Aufgaben wurden automatisch erzeugt.")
        },
        onError: (err) => {
            messageError("catalog-create", "Fehler!", apiErrorMessage(err, "Die Leistung konnte nicht angelegt werden."))
        },
    })

export const useUpdateServiceCatalogItemMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: {
            id: string
            activity?: string
            description?: string
            intervalWeeks?: number
            delayWeeks?: number
            active?: boolean
        }) =>
            updateServiceCatalogItem(data.id, {
                activity: data.activity,
                description: data.description,
                intervalWeeks: data.intervalWeeks,
                delayWeeks: data.delayWeeks,
                active: data.active,
            }),
        onSuccess: () => {
            refreshCatalog(queryClient)
            messageSuccess("catalog-update", "Leistung aktualisiert", "Die Änderung wurde übernommen.")
        },
        onError: (err) => {
            messageError("catalog-update", "Fehler!", apiErrorMessage(err, "Die Leistung konnte nicht gespeichert werden."))
        },
    })

export const useDeleteServiceCatalogItemMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (id: string) => deleteServiceCatalogItem(id),
        onSuccess: () => {
            refreshCatalog(queryClient)
            messageSuccess("catalog-delete", "Leistung entfernt", "Die Position wurde gelöscht.")
        },
        onError: (err) => {
            messageError("catalog-delete", "Fehler!", apiErrorMessage(err, "Die Leistung konnte nicht gelöscht werden."))
        },
    })

export const useGenerateTourTasksMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (objectId: string) => generateTourTasks(objectId),
        onSuccess: (data) => {
            refreshCatalog(queryClient)
            messageSuccess("catalog-generate", "Aufgaben erzeugt", `${data.created} Aufgabe(n) für die nächsten ${data.weeksAhead} Wochen angelegt.`)
        },
        onError: (err) => {
            messageError("catalog-generate", "Fehler!", apiErrorMessage(err, "Die Aufgaben konnten nicht erzeugt werden."))
        },
    })

export const useDeleteTourTasksForObjectMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (objectId: string) => deleteTourTasksForObject(objectId),
        onSuccess: (data) => {
            refreshCatalog(queryClient)
            messageSuccess("tour-task-delete-all", "Aufgaben gelöscht", `${data.deleted} Aufgabe(n) an diesem Objekt wurden entfernt.`)
        },
        onError: (err) => {
            messageError("tour-task-delete-all", "Fehler!", apiErrorMessage(err, "Die Aufgaben konnten nicht gelöscht werden."))
        },
    })

export const useUpdateTourObjectTaskMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { id: string; status: TourObjectTaskStatus; rejectionComment?: string }) =>
            updateTourObjectTask(data.id, { status: data.status, rejectionComment: data.rejectionComment }),
        onSuccess: (_data, variables) => {
            refreshCatalog(queryClient)
            if (variables.status === "done") messageSuccess("tour-task-done", "Aufgabe erledigt", "Die Aufgabe ist abgeschlossen.")
            if (variables.status === "rejected") messageSuccess("tour-task-reject", "Aufgabe abgelehnt", "Die Begründung wurde gespeichert.")
            if (variables.status === "dismissed") messageSuccess("tour-task-dismiss", "Aufgabe ausgetragen", "Die Aufgabe erscheint nicht mehr in der Wiedervorlage.")
            if (variables.status === "open") messageSuccess("tour-task-open", "Aufgabe geöffnet", "Die Aufgabe ist wieder offen.")
        },
        onError: (err) => {
            messageError("tour-task-update", "Fehler!", apiErrorMessage(err, "Die Aufgabe konnte nicht aktualisiert werden."))
        },
    })
